import type { ReactNode } from 'react'
import { Mtds, templates } from 'prove-common'
import type {
  FieldTemplateProps,
  UiSchema,
  WidgetProps
} from 'prove-common/rjsf'

export type PlanField = 'due' | 'contractRef' | 'purposeId' | 'workspaceId'

// Only own values participate in RJSF validation. Keep inherited-mode drafts
// separately so any widget/default updates cannot replace a user's saved draft.
export function activePlanFormData<
  T extends Partial<Record<PlanField, unknown>>
>(
  draft: T | undefined,
  inherited: Partial<Record<PlanField, boolean>>
): T | undefined {
  if (draft == null) return draft
  const active = { ...draft }
  for (const field of Object.keys(inherited) as PlanField[]) {
    if (inherited[field]) delete active[field]
  }
  return active
}

export function retainInheritedPlanDrafts<
  T extends Partial<Record<PlanField, unknown>>
>(
  active: T,
  draft: T | undefined,
  inherited: Partial<Record<PlanField, boolean>>
): T {
  const next = { ...active }
  for (const field of Object.keys(inherited) as PlanField[]) {
    if (inherited[field]) next[field] = draft?.[field]
  }
  return next
}

export const planFieldLabels: Record<PlanField, string> = {
  due: 'Frist',
  contractRef: 'Referanse til lab',
  purposeId: 'Hensikt',
  workspaceId: 'Arbeidsområde'
}

export function planInheritanceControls({
  hasParent,
  inherited,
  disabled,
  onChange
}: {
  hasParent: boolean
  inherited: Partial<Record<PlanField, boolean>>
  disabled: Partial<Record<PlanField, boolean>>
  onChange: (field: Exclude<PlanField, 'workspaceId'>, checked: boolean) => void
}): Partial<Record<PlanField, ReactNode>> {
  if (!hasParent) return {}
  const fields = ['due', 'contractRef', 'purposeId'] as const
  return Object.fromEntries(
    fields.map((field) => [
      field,
      <Mtds.Grid key={field} data-gap='2'>
        <FieldInheritanceToggle
          field={field}
          inherited={inherited[field] === true}
          disabled={disabled[field]}
          onChange={(checked) => onChange(field, checked)}
        />
      </Mtds.Grid>
    ])
  )
}

// Keep the RJSF widget, label and validation together. The checkbox is a sibling
// Field, not a second input inside the design system's single-input Field.
export function PlanInheritanceFieldTemplate(props: FieldTemplateProps) {
  return (
    <Mtds.Grid
      data-gap='2'
      className={props.classNames}
      hidden={props.hidden}
      role='group'
      aria-label={props.label}
    >
      <templates.FieldTemplate {...props} />
      {props.uiSchema?.['ui:options']?.inheritanceControl as ReactNode}
    </Mtds.Grid>
  )
}

export function withPlanInheritanceControls(
  uiSchema: UiSchema,
  controls: Partial<Record<PlanField, ReactNode>>,
  inherited: Partial<Record<PlanField, boolean>>,
  displayValues: Partial<Record<PlanField, string>>
): UiSchema {
  const result = { ...uiSchema }
  for (const field of Object.keys(controls) as PlanField[]) {
    result[field] = {
      ...uiSchema[field],
      ...(inherited[field] ? { 'ui:widget': InheritedPlanValueWidget } : {}),
      'ui:FieldTemplate': PlanInheritanceFieldTemplate,
      'ui:options': {
        ...uiSchema[field]?.['ui:options'],
        inheritanceControl: controls[field],
        inheritedDisplayValue: displayValues[field]
      }
    }
  }
  return result
}

// Presentation only: never call onChange or insert these values into formData.
// A single disabled control also displays obsolete enums without validating them.
export function InheritedPlanValueWidget({ id, schema, options }: WidgetProps) {
  return (
    <Mtds.Input
      id={id}
      type={schema.format === 'date' ? 'date' : 'text'}
      value={String(options.inheritedDisplayValue ?? '')}
      disabled
      readOnly
    />
  )
}

type FieldInheritanceToggleProps = {
  field: PlanField
  inherited: boolean
  disabled?: boolean
  onChange: (inherited: boolean) => void
}

export function FieldInheritanceToggle({
  field,
  inherited,
  disabled,
  onChange
}: Readonly<FieldInheritanceToggleProps>) {
  return (
    <Mtds.Field
      as='input'
      type='checkbox'
      label='Arv fra overordnet plan'
      aria-label={`Arv fra overordnet plan – ${planFieldLabels[field]}`}
      checked={inherited}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
  )
}
